/**
 * The drawing surface shared by the handwriting pad and calibration: a
 * canvas that turns pen / touch / mouse pointers into strokes (in pad units,
 * see ink.ts) and renders them as smooth, round-capped ink.
 *
 * Controlled: finished strokes are reported with onStrokeEnd and come back in
 * `strokes`; only the stroke being drawn lives here, in a ref, so pointer
 * moves never re-render React.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { HwPoint, HwStroke } from '../contracts.ts';
import { PointerArbiter, appendPoint, inkWidth, pointerKind, pxToUnits, smoothPath, unitsToPx } from './ink.ts';
import './handwriting.css';

export interface InkCanvasProps {
  /** Finished strokes, in pad units. */
  strokes: readonly HwStroke[];
  disabled?: boolean;
  /** Accessible name of the writing area. */
  label: string;
  /** Faint baseline to write on. */
  guide?: boolean;
  /** Shown faintly in the middle while the area is empty. */
  placeholder?: string;
  className?: string;
  onStrokeStart?(): void;
  onStrokeEnd(stroke: HwStroke): void;
}

/** Used when the theme hasn't set --ds-block-text (e.g. before the first theme loads). */
const FALLBACK_INK = '#1f2544';

interface PointerSample {
  clientX: number;
  clientY: number;
  timeStamp: number;
}

export function InkCanvas(props: InkCanvasProps) {
  const { strokes, disabled = false, label, guide = false, placeholder, className } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const propsRef = useRef(props);
  const size = useRef({ w: 0, h: 0, dpr: 1 });
  const active = useRef<HwPoint[] | null>(null);
  const rect = useRef<DOMRect | null>(null);
  const frame = useRef(0);
  const [arbiter] = useState(() => new PointerArbiter());
  const [drawing, setDrawing] = useState(false);

  useLayoutEffect(() => {
    propsRef.current = props;
  });

  const draw = useCallback(() => {
    frame.current = 0;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const { w, h, dpr } = size.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    if (h <= 0) return;
    const ink = getComputedStyle(canvas).getPropertyValue('--ds-block-text').trim() || FALLBACK_INK;
    const width = inkWidth(h);
    ctx.strokeStyle = ink;
    ctx.fillStyle = ink;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const px = (u: number) => unitsToPx(u, h);
    const all = active.current ? [...propsRef.current.strokes, active.current] : propsRef.current.strokes;
    for (const stroke of all) {
      const ops = smoothPath(stroke);
      ctx.beginPath();
      for (const o of ops) {
        if (o.op === 'dot') ctx.arc(px(o.x), px(o.y), width * 0.6, 0, Math.PI * 2);
        else if (o.op === 'move') ctx.moveTo(px(o.x), px(o.y));
        else if (o.op === 'line') ctx.lineTo(px(o.x), px(o.y));
        else ctx.quadraticCurveTo(px(o.cx), px(o.cy), px(o.x), px(o.y));
      }
      if (ops[0]?.op === 'dot') ctx.fill();
      else ctx.stroke();
    }
  }, []);

  const schedule = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(draw);
  }, [draw]);

  // Keep the backing store matched to the on-screen size and pixel ratio.
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const measure = () => {
      const dpr = Math.min(3, window.devicePixelRatio || 1);
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      size.current = { w, h, dpr };
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      draw();
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw]);

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  // Redraw before paint whenever the finished strokes change.
  useLayoutEffect(draw, [strokes, draw]);

  // Disabling mid-stroke drops the stroke.
  useEffect(() => {
    if (!disabled) return;
    arbiter.reset();
    if (active.current) {
      active.current = null;
      setDrawing(false);
      schedule();
    }
  }, [disabled, arbiter, schedule]);

  const toPoint = (e: PointerSample): HwPoint => {
    const r = rect.current;
    const left = r?.left ?? 0;
    const top = r?.top ?? 0;
    const h = r?.height || size.current.h;
    return { x: pxToUnits(e.clientX - left, h), y: pxToUnits(e.clientY - top, h), t: e.timeStamp };
  };

  const finish = (keep: boolean) => {
    const stroke = active.current;
    active.current = null;
    rect.current = null;
    setDrawing(false);
    if (keep && stroke?.length) propsRef.current.onStrokeEnd(stroke);
    schedule();
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (propsRef.current.disabled || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const decision = arbiter.down(e.pointerId, pointerKind(e.pointerType), e.timeStamp);
    if (decision === 'ignore') return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // The pointer may already be gone (fast taps on some browsers); drawing still works without capture.
    }
    rect.current = e.currentTarget.getBoundingClientRect();
    active.current = [toPoint(e)];
    setDrawing(true);
    propsRef.current.onStrokeStart?.();
    schedule();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.pointerType === 'pen') arbiter.notePen(e.timeStamp);
    const stroke = active.current;
    if (!stroke || !arbiter.owns(e.pointerId)) return;
    const native = e.nativeEvent;
    // Coalesced events carry the full-rate pen samples between animation frames: smoother ink.
    const coalesced = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    for (const sample of coalesced.length ? coalesced : [native]) appendPoint(stroke, toPoint(sample));
    schedule();
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (arbiter.up(e.pointerId, e.timeStamp)) finish(true);
  };

  const onPointerCancel = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (arbiter.up(e.pointerId)) finish(false);
  };

  const classes = ['hw-ink', className, disabled && 'hw-ink--disabled'].filter(Boolean).join(' ');
  const showPlaceholder = !!placeholder && !drawing && strokes.length === 0;

  return (
    <div ref={wrapRef} className={classes}>
      {guide && <div className="hw-ink__guide" aria-hidden="true" />}
      {showPlaceholder && (
        <span className="hw-ink__placeholder" aria-hidden="true">
          {placeholder}
        </span>
      )}
      <canvas
        ref={canvasRef}
        className="hw-ink__canvas"
        role="img"
        aria-label={label}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onLostPointerCapture={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  );
}
